import { useCallback, useState } from 'react';
import { useGlobal } from "../context/globalProvider";
import { skipCharge, unskipCharge, removeDiscountsFromCharge, applyDiscountToCharge, removeDiscountsFromAddress, applyDiscountToAddress, processCharge, getCharge, listOnetimes, deleteOnetime } from "@rechargeapps/storefront-client";
import { toast } from "../hooks/useToast";

export const useCharge = () => {
  const { session, refreshData, sendEvent, orders, dispatch } = useGlobal();
  const currentDiscounts = orders?.upcoming?.flatMap(charge => charge.discounts) || [];
  const [isLoading, setIsLoading] = useState(false);

  const skipChargeData = useCallback(async (chargeId: string, purchaseItemIds: (string | number)[], skipRefresh: boolean = false, charge?: any) => {
    if (!session) return;
    setIsLoading(true);
    try {
      // Filter out onetime items - they cannot be skipped with skipCharge API
      const subscriptionItemIds: (string | number)[] = [];
      const onetimeItemIds: (string | number)[] = [];
      
      if (charge?.line_items) {
        purchaseItemIds.forEach(itemId => {
          const lineItem = charge.line_items.find((item: any) => item.purchase_item_id === itemId);
          if (lineItem?.purchase_item_type === 'onetime') {
            onetimeItemIds.push(itemId);
          } else {
            subscriptionItemIds.push(itemId);
          }
        });
      } else {
        // Fallback: assume all are subscription items if charge data not available
        subscriptionItemIds.push(...purchaseItemIds);
      }

      // Skip subscription items using the regular skipCharge API
      if (subscriptionItemIds.length > 0) {
        await skipCharge(session, chargeId, subscriptionItemIds);
      }

      // Note: Onetime items need to be handled separately using updateOnetime API
      // This will be done in the component using useOnetime hook
      
      if (!skipRefresh) {
        await refreshData();
        sendEvent('charge_skipped', { 
          chargeId, 
          subscriptionItemIds, 
          onetimeItemIds,
          original_discount_codes: currentDiscounts 
        });
        
        if (subscriptionItemIds.length > 0) {
          toast({
            title: "Subscription items skipped successfully",
            description: "The subscription items have been skipped."
          });
        }
      }

      return { subscriptionItemIds, onetimeItemIds };
    } catch (error) {
      toast({
        title: "Error skipping charge",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData, sendEvent, currentDiscounts]);

  const unskipChargeData = useCallback(async (chargeId: string, purchaseItemIds: (string | number)[], skipRefresh: boolean = false) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await unskipCharge(session, chargeId, purchaseItemIds);
      if (!skipRefresh) {
        await refreshData();
        sendEvent('charge_unskipped', { chargeId, purchaseItemIds, original_discount_codes: currentDiscounts });
        toast({
          title: "Charge unskipped successfully",
          description: "The charge has been unskipped."
        });
      }
    } catch (error) {
      toast({
        title: "Error unskipping charge",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData, sendEvent, currentDiscounts]);

  const removeDiscountData = useCallback(async (
    addressId: string, 
    chargeId: string, 
    showToast: boolean = true,
    chargeDiscounts: string[] = [],
  ) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await removeDiscountsFromAddress(session, addressId, { commit: true });

      if (chargeDiscounts.length > 0) {
        await removeDiscountsFromCharge(session, chargeId);
      }

      await refreshData();
      sendEvent('discount_removed', { addressId, chargeId });
      if (showToast) {
        toast({
          title: "Discount removed successfully",
          description: "The discount has been removed."
        });
      }
    } catch (error) {
      toast({
        title: "Error removing discount",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData, sendEvent, currentDiscounts]);

  const addDiscountToAddress = useCallback(async (addressId: string, discountCode: string) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await applyDiscountToAddress(session, addressId, discountCode);
      await refreshData();
      sendEvent('discount_added_to_address', { addressId, discountCode, original_discount_codes: currentDiscounts });
    } catch (error) {
      toast({
        title: "Error adding discount",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData, sendEvent, currentDiscounts]);

  const addDiscount = useCallback(async (chargeId: string, discountCode: string) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await applyDiscountToCharge(session, chargeId, discountCode);
      await refreshData();
      sendEvent('discount_added_to_charge', { chargeId, discountCode, original_discount_codes: currentDiscounts });
      toast({
        title: "Discount added successfully",
        description: "The discount has been applied."
      });
      return true;
    } catch (error) {
      toast({
        title: "Error adding discount",
        description: error.message,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData, sendEvent, currentDiscounts]);

  const processChargeData = useCallback(async (chargeId: string) => {
    if (!session) return;
    setIsLoading(true);
    try {
      await processCharge(session, chargeId);

      // Create a promise that resolves when polling is complete
      await new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 20;

        const checkChargeProcessed = async () => {
          try {
            const charge = await getCharge(session, chargeId);
            if (charge.processed_at) {
              await refreshData();
              dispatch({ type: 'SET_PROCESSED_ORDER', payload: charge });
              sendEvent('charge_processed', { chargeId, original_discount_codes: currentDiscounts });
              toast({
                title: "Charge processed successfully",
                description: "The charge has been processed."
              });
              resolve(true);
              return;
            }

            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(checkChargeProcessed, 1000);
            } else {
              toast({
                title: "Charge processing timeout",
                description: "The charge processing status could not be confirmed.",
                variant: "destructive",
              });
              reject(new Error("Charge processing timeout"));
            }
          } catch (error) {
            toast({
              title: "Error checking charge status",
              description: error.message,
              variant: "destructive",
            });
            reject(error);
          }
        }

        setTimeout(checkChargeProcessed, 1000);
      });

    } catch (error) {
      toast({
        title: "Error processing charge",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [session, refreshData, sendEvent, currentDiscounts]);

  return {
    skipCharge: skipChargeData,
    unskipCharge: unskipChargeData,
    removeDiscount: removeDiscountData,
    addDiscount: addDiscount,
    addDiscountToAddress: addDiscountToAddress,
    processCharge: processChargeData,
    isLoading,
  };
};